import { mainMenus } from "./menus/main-menus";
import { subMenus } from "./menus/sub-menus";

import _every from "lodash/every";
import _filter from "lodash/filter";
import _forEach from "lodash/forEach";
import _includes from "lodash/includes";
import _cloneDeep from "lodash/cloneDeep";

const _products = new WeakMap();
const _permissions = new WeakMap();
const _conditions = new WeakMap();

export class SidebarMenu {
    constructor(data) {
        const { products, permissions, conditions } = data;

        _products.set(this, products);
        _conditions.set(this, conditions);
        _permissions.set(this, permissions);
    }

    getConditions() {
        return _conditions.get(this);
    }

    getProducts() {
        return _products.get(this);
    }

    getPermissions() {
        return _permissions.get(this);
    }

    willCheckMenuPermissions(menu) {
        const _menu = _cloneDeep(menu);
        const conditions = this.getConditions();
        const permissions = this.getPermissions();
        const accountProducts = this.getProducts();

        if (_menu.hasOwnProperty("conditions")) {
            _menu.show = _every(_menu.conditions, condition => conditions[condition]);
        }

        if (_menu.hasOwnProperty("permissions")) {
            let hasPermission = false;
            const {
                isReseller,
                hasProducts,
                mobileWorker,
                hasHOSReports,
                hasMapFeatures,
                hasIFTAReports,
                hasEldFeatures,
                hasDvirFeatures,
                hideFuelReceipts,
                hideLogShowEdits,
                licenseManagement,
                hasWorkOrderReports,
                hasWorkOrderFeatures,
                allowSubAccountBranding
            } = conditions;

            // Iterate through each menus permission then check the permission value if the user has permission
            _menu.permissions.forEach((menuPermission) => {
                hasPermission = hasPermission || permissions[menuPermission];
            });

            _menu.show = hasPermission;
            if (_menu.parentAlias === "hos") {
                if (_menu.slug === "locations") {
                    _menu.show = hasMapFeatures && hasPermission;
                } else if (_menu.slug === "reports") {
                    _menu.show = hasHOSReports && hasPermission;
                } else if (_menu.slug === "log-edits") {
                    _menu.show = !hideLogShowEdits && hasPermission;
                }
            }

            if (_menu.slug === "fuel-receipts" && _menu.parentAlias === "ifta") {
                // This will hide the fuel receipts sub-menu if the logged in user is not an admin or not a parent account.
                _menu.show = !hideFuelReceipts;
            } else if (_menu.slug === "reports" && _menu.parentAlias === "ifta") {
                _menu.show = hasIFTAReports && hasPermission;
            } else if (
                _menu.slug === "mobile-worker-reports" &&
                _menu.parentAlias === "mobile-worker"
            ) {
                _menu.show = hasWorkOrderReports && hasPermission;
            }

            if (_menu.parentAlias === "settings") {
                _menu.show = false;

                if (_menu.slug === "accounts") {
                    _menu.show =
                        (isReseller &&
                            hasPermission &&
                            (hasWorkOrderFeatures || hasDvirFeatures || hasEldFeatures)) ||
                        false;
                } else if (_menu.slug === "account-settings") {
                    _menu.show =
                        (hasProducts &&
                            hasPermission &&
                            (hasWorkOrderFeatures || hasDvirFeatures || hasEldFeatures)) ||
                        false;
                } else if (_menu.slug === "carrier-information") {
                    _menu.show =
                        (hasPermission &&
                            (hasWorkOrderFeatures || hasDvirFeatures || hasEldFeatures)) ||
                        false;
                } else if (_menu.slug === "alerts") {
                    _menu.show = (hasPermission && (hasEldFeatures || hasDvirFeatures)) || false;
                } else if (_menu.slug === "branding") {
                    _menu.show =
                        (hasProducts &&
                            allowSubAccountBranding &&
                            (hasEldFeatures || hasDvirFeatures || hasWorkOrderFeatures) &&
                            hasPermission) ||
                        false;
                } else if (["users", "user-profile"].includes(_menu.slug)) {
                    _menu.show =
                        (hasProducts &&
                            (hasWorkOrderFeatures || hasDvirFeatures || hasEldFeatures)) ||
                        false;
                } else if (_menu.slug === "user-roles") {
                    _menu.show =
                        (hasPermission &&
                            (hasEldFeatures || hasDvirFeatures || hasWorkOrderFeatures)) ||
                        false;
                } else if (_menu.slug === "form-setup") {
                    _menu.show = (hasPermission && mobileWorker && hasWorkOrderFeatures) || false;
                } else if (_menu.slug === "user-interface") {
                    _menu.show =
                        (hasProducts &&
                            (hasEldFeatures || hasDvirFeatures || hasWorkOrderFeatures)) ||
                        false;
                } else if (_menu.slug === "license-management") {
                    _menu.show = licenseManagement;
                } else if (
                    ["equipment", "terminals", "button-editor"].includes(_menu.slug)
                ) {
                    _menu.show =
                        (hasPermission && hasProducts && (hasDvirFeatures || hasEldFeatures)) ||
                        false;
                } else if (["driver-settings", "driver-exceptions"].includes(_menu.slug)) {
                    _menu.show =
                        (hasPermission &&
                            hasProducts &&
                            (hasEldFeatures || accountProducts.dvirOnly)) ||
                        false;
                } else if (_menu.slug === "dvir-form") {
                    _menu.show = (hasPermission && hasProducts && hasDvirFeatures) || false;
                } else if (_menu.slug === "push-data") {
                    _menu.show = (hasEldFeatures && hasPermission) || false;
                } else if (_menu.slug === "push-notifications") {
                    _menu.show = (hasEldFeatures && hasPermission) || false;
                } else if (_menu.slug === "support-contact") {
                    _menu.show = (hasEldFeatures && hasPermission) || false;
                } else if (_menu.slug === "download-resources") {
                    _menu.show = (hasProducts &&
                            (hasWorkOrderFeatures || hasDvirFeatures || hasEldFeatures)) ||
                        false;
                } else if (_menu.slug === "upload-resources") {
                    _menu.show = (hasEldFeatures && hasPermission) || false;
                }
            }
        }

        return _menu;
    }

    willGenerateParentMenus(menuNames) {
        const collection = [],
            parentMenus = mainMenus();

        _forEach(menuNames, menuName => {
            const parentMenu = _filter(parentMenus, { alias: menuName })[0];

            if (parentMenu) {
                const menu = this.willCheckMenuPermissions(parentMenu);
                collection.push(menu);
            }
        });

        return collection;
    }

    willGenerateSubMenus(parentMenus, menuNames) {
        _forEach(parentMenus, parentMenu => {
            const inArray = _includes(menuNames, parentMenu.alias);

            if (inArray) {
                const childMenus = subMenus[can.camelCase(parentMenu.alias)]();
                _forEach(childMenus, childMenu => {
                    parentMenu.subMenus.push(this.willCheckMenuPermissions(childMenu));
                });
            }
        });

        return parentMenus;
    }

    getAdditionalMenu(menus) {
        const conditions = this.getConditions();

        if (conditions.isUserRoleVTAdmin) {
            menus.push("admin-tools");
        }

        return menus;
    }

    willGenerateMenusListBy(products) {
        if (products.hasOwnProperty("hosOnly") && products.hosOnly) {
            return this.getAdditionalMenu(["hos", "ifta", "settings"]);
        }

        if (products.hasOwnProperty("dvirOnly") && products.dvirOnly) {
            return this.getAdditionalMenu(["dvir", "settings"]);
        }

        if (products.hasOwnProperty("mobileWorkerOnly") && products.mobileWorkerOnly) {
            return this.getAdditionalMenu(["mobile-worker", "settings"]);
        }

        if (products.hasOwnProperty("hosAndDvir") && products.hosAndDvir) {
            return this.getAdditionalMenu(["hos", "ifta", "dvir", "settings"]);
        }

        if (products.hasOwnProperty("hosAndMobileWorker") && products.hosAndMobileWorker) {
            return this.getAdditionalMenu(["hos", "ifta", "mobile-worker", "settings"]);
        }

        if (products.hasOwnProperty("hosDvirAndMobileWorker") && products.hosDvirAndMobileWorker) {
            return this.getAdditionalMenu(["hos", "ifta", "dvir", "mobile-worker", "settings"]);
        }

        if (products.hasOwnProperty("dvirAndMobileWorker") && products.dvirAndMobileWorker) {
            return this.getAdditionalMenu(["dvir", "mobile-worker", "settings"]);
        }

        if (Vistracks.myAccount.features.ifta) {
            return this.getAdditionalMenu(["ifta", "settings"]);
        }

        return this.getAdditionalMenu(["settings"]);
    }

    create() {

        // 1. Determine the product
        const products = this.getProducts();

        // 2. Get the list of parent menus to be created base on product
        const menusToCreate = this.willGenerateMenusListBy(products);

        // 3. Generate the parent menu base on the determined product
        const parentMenus = this.willGenerateParentMenus(menusToCreate);

        // 4. Generate its sub-menu
        const subMenus = this.willGenerateSubMenus(parentMenus, menusToCreate);

        return subMenus;
    }
}
